import React, { useState, useEffect } from "react";
import {
  MdFlag,
  MdCheck,
  MdChevronRight,
  MdLock,
  MdExpandLess,
  MdExpandMore,
  MdPlayArrow,
  MdHourglassEmpty,
} from "react-icons/md";
import Card from "components/card";
import CircularProgressMini from "components/charts/CircularProgressMini";
import ErrorAlerts from "components/alerts/ErrorAlerts";
import SuccessAlerts from "components/alerts/SuccessAlerts";
import {
  fetchTeamStepStatus,
  createApprovalRequest,
  getUserProfile,
  claimPoap,
  checkPoapClaimed,
} from "api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import MintConfirmationModal from "components/modal/MintConfirmationModal";

const normalizeStatus = (s) => {
  // Treat backend approvals as completed for UI logic
  if (s === "approved" || s === "verified") return "completed";
  return s;
};

const Completed = ({ poap, poapIndex, teamId }) => {
  const { id: descriptionId, title, learnings = "", totalPoints = 0, categories = [] } = poap || {};
  const [isTeamLeader, setIsTeamLeader] = useState(false);
  const [userHasTeam, setUserHasTeam] = useState(false);
  const [expandedTopics, setExpandedTopics] = useState({});
  const [expandedSteps, setExpandedSteps] = useState({});
  const [loadingSteps, setLoadingSteps] = useState({});
  const [errorMessages, setErrorMessages] = useState([]);
  const [successMessage, setSuccessMessage] = useState(null);

  const [showMintModal, setShowMintModal] = useState(false);
  const [mintingLoading, setMintingLoading] = useState(false);
  const [alreadyMinted, setAlreadyMinted] = useState(false);

  const validCategories = categories.map((category) => ({
    name: category.name || "Unnamed Category",
    steps: category.steps ? category.steps.sort((a, b) => a.id - b.id) : [],
  }));

  const queryClient = useQueryClient();



  useEffect(() => {
    if (successMessage) {
      const t = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(t);
    }
  }, [successMessage]);



  // Profile
  const {
    data: userProfileData,
  } = useQuery({
    queryKey: ["userProfile"],
    queryFn: getUserProfile,
  });
  const userProfile = userProfileData?.user;
  const userEmail = userProfile?.email;

  useEffect(() => {
    if (userProfile) {
      setIsTeamLeader(userProfile.teamRole === "Team Leader");
      setUserHasTeam(!!userProfile.team?.id);
    }
  }, [userProfile]);

  // Has user already minted this POAP?
  useEffect(() => {
    if (userEmail && title) {
      const trimmed = title.trim().replace(" POAP", "");
      checkPoapClaimed({ address: userEmail, poapName: trimmed })
        .then((data) => {
          if (data?.claim) setAlreadyMinted(true);
          else return checkPoapClaimed({ address: userEmail, poapName: `${trimmed} POAP` });
        })
        .then((data2) => {
          if (data2?.claim) setAlreadyMinted(true);
        })
        .catch(() => {});
    }
  }, [userEmail, title]);

  // Team step statuses (server source of truth)
  const { data: teamStepsData } = useQuery({
    queryKey: ["teamSteps", teamId, descriptionId],
    queryFn: () => fetchTeamStepStatus(teamId, descriptionId),
    enabled: !!teamId && !!descriptionId,
    refetchInterval: 10000,
    refetchOnWindowFocus: true,
  });

  const [teamSteps, setTeamSteps] = useState({});
  useEffect(() => {
    if (teamStepsData) {
      const map = teamStepsData.reduce((acc, row) => {
        acc[row.poapStepId] = normalizeStatus(row.status);
        return acc;
      }, {});
      setTeamSteps(map);
    }
  }, [teamStepsData]);



  const handleRequestApproval = async (stepId) => {
    if (!isTeamLeader || !userHasTeam) {
      setErrorMessages([
        "Only team leaders can request step approvals. Reach out to your team leader to proceed.",
      ]);
      setTimeout(() => setErrorMessages([]), 5000);
      return;
    }
    // If server says it's already waiting, don't send again
    if (teamSteps[stepId] === "waiting_for_approval") return;

    setLoadingSteps((p) => ({ ...p, [stepId]: true }));
    setErrorMessages([]);
    setSuccessMessage(null);
    try {
      // Use React Query mutation
      await createApprovalRequest(stepId, teamId);
      // no local “waiting” cache — we wait for server polling to update
    } catch (error) {
      // Only clear loading state on error
      setLoadingSteps((p) => ({ ...p, [stepId]: false }));
      throw error; // Re-throw to trigger onError in the mutation
    }
  };

  const toggleTopic = (i) => setExpandedTopics((p) => ({ ...p, [i]: !p[i] }));
  const toggleStep = (ci, si) => {
    const key = `${ci}-${si}`;
    setExpandedSteps((p) => ({ ...p, [key]: !p[key] }));
  };

  const areAllStepsCompleted = (steps) =>
    steps.every((s) => teamSteps[s.id] === "completed");

  const isStepUnlocked = (categorySteps, stepIndex) => {
    if (stepIndex === 0) return true;
    const prev = categorySteps[stepIndex - 1];
    return teamSteps[prev.id] === "completed";
    // If you want “waiting” to count as unlock for next step, include it:
    // return ["completed", "waiting_for_approval"].includes(teamSteps[prev.id]);
  };

  const getStepStatusIcon = (status, unlocked, isCurrentStep) => {
    if (!unlocked) return <MdLock />;
    if (status === "completed") return <MdCheck />;
    if (status === "waiting_for_approval") return <MdHourglassEmpty className="animate-spin" />;
    if (isCurrentStep) return <MdPlayArrow />;
    return <MdLock />;
  };

  const calculateStepProgress = (status, unlocked, isCurrentStep) => {
    if (!unlocked) return 0;
    if (status === "completed") return 100;
    return 0;
  };

  const isCurrentPoapCompleted = validCategories.every((c) => areAllStepsCompleted(c.steps));
  useEffect(() => {
    if (poap) poap.isCompleted = isCurrentPoapCompleted;
  }, [isCurrentPoapCompleted, poap]);

  const handleCloseError = () => setErrorMessages([]);
  const handleCloseSuccess = () => setSuccessMessage(null);

  const handleMintPoap = () => {
    if (!alreadyMinted) setShowMintModal(true);
  };

  const handleConfirmMint = async (selectedAddress) => {
    setMintingLoading(true);
    try {
      await claimPoap({ address: selectedAddress.trim(), poapName: title });
      setSuccessMessage("POAP claimed successfully!");
      setAlreadyMinted(true);
    } catch {
      setErrorMessages(["Error minting POAP. Please try again later."]);
    } finally {
      setMintingLoading(false);
      setShowMintModal(false);
    }
  };

  const handleCancelMint = () => setShowMintModal(false);

  return (
    <div>
      {errorMessages.length > 0 && <ErrorAlerts errors={errorMessages} onClose={handleCloseError} />}
      {successMessage && <SuccessAlerts message={successMessage} onClose={handleCloseSuccess} />}

      <Card extra={"w-full p-4"}>
        <h4 className="text-xl font-bold text-navy-700 dark:text-white">{title}</h4>

        <div className="mt-3 flex items-center justify-between">
          <p className="text-base font-medium uppercase text-gray-600">
            {validCategories.length} {validCategories.length > 1 ? "Topics" : "Topic"}
          </p>
          <p className={`text-lg ${isCurrentPoapCompleted ? "text-green-500" : "text-orange-500"}`}>
            <MdFlag />
          </p>
        </div>

        <div className="mt-[20px] grid w-full grid-cols-5 gap-2">
          {validCategories.map((category, index) => (
            <div
              key={index}
              className={`h-2 w-full rounded-[36px] ${
                areAllStepsCompleted(category.steps) ? "bg-green-500" : "bg-orange-500"
              }`}
            />
          ))}
        </div>

        <div>
          {validCategories.map((category, catIndex) => (
            <div key={catIndex}>
              <h5
                className="mt-4 text-lg font-bold text-navy-700 dark:text-white flex items-center justify-between cursor-pointer"
                onClick={() => toggleTopic(catIndex)}
              >
                {category.name}
                {category.steps.length > 1 && (expandedTopics[catIndex] ? <MdExpandLess /> : <MdExpandMore />)}
              </h5>

              {(expandedTopics[catIndex] || category.steps.length === 1) && (
                <div>
                  {category.steps.map((step, stepIndex) => {
                    const unlocked = isStepUnlocked(category.steps, stepIndex);
                    const stepKey = `${catIndex}-${stepIndex}`;
                    const status = teamSteps[step.id] || "not_started";
                    const isCurrentStep = unlocked && status !== "completed";

                    return (
                      <div key={stepIndex}>
                        <div
                          className="mt-[19px] flex items-center justify-between gap-2"
                          onClick={() => unlocked && toggleStep(catIndex, stepIndex)}
                        >
                          <div className="flex gap-2">
                            <div className="w-[30px]">
                              <CircularProgressMini
                                step={stepIndex + 1}
                                percentage={calculateStepProgress(status, unlocked, isCurrentStep)}
                                color={isCurrentStep ? "orange" : status === "completed" ? "green" : "gray"}
                              />
                            </div>
                            <div className="flex items-center gap-1 font-medium text-gray-600 dark:text-white">
                              <p>
                                {step.step} <b>({step.points} points)</b>
                              </p>
                              <p className="mt-1 text-lg">
                                {getStepStatusIcon(status, unlocked, isCurrentStep)}
                              </p>
                            </div>
                          </div>
                          <p className="text-2xl text-gray-600 dark:text-white">
                            {expandedSteps[stepKey] && unlocked ? <MdExpandLess /> : <MdChevronRight />}
                          </p>
                        </div>

                        {expandedSteps[stepKey] && unlocked && (
                          <div className="ml-8 mt-2 text-sm text-gray-600 dark:text-gray-300">
                            {step.detail}
                            {status !== "completed" && isCurrentStep && (
                              <div className="mt-2">
                                <button
                                  className="text-sm text-brand-500 hover:underline focus:outline-none flex items-center gap-1"
                                  onClick={() => handleRequestApproval(step.id, step.step)}
                                  disabled={loadingSteps[step.id] || status === "waiting_for_approval"}
                                >
                                  {loadingSteps[step.id] || status === "waiting_for_approval" ? (
                                    <>
                                      <MdHourglassEmpty className="animate-spin" />
                                      Waiting for Approval
                                    </>
                                  ) : (
                                    "Request Approval"
                                  )}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8">
          <h5 className="text-xl font-bold text-navy-700 dark:text-white">What you’ll learn</h5>
          <p className="mt-3 text-navy-700 dark:text-white">{learnings}</p>
        </div>

        <div className="mt-[52px]">
          <h5 className="mb-3 text-xl font-bold text-navy-700 dark:text-white">By the numbers</h5>
          <div className="flex w-max flex-wrap items-center justify-between gap-5 lg:w-full">
            <div>
              <div className="flex items-center gap-1">
                <h5 className="text-gray-600">Total Points:</h5>
                <p className="font-medium text-navy-700 dark:text-white">{totalPoints}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-[52px]">
          <h5 className="text-xl font-bold text-navy-700 dark:text-white">Mint Your Badge</h5>
          <div className="relative group">
            <button
              onClick={() => !alreadyMinted && setShowMintModal(true)}
              disabled={!isCurrentPoapCompleted || mintingLoading || alreadyMinted}
              className={`flex items-center justify-center w-full px-6 py-3 rounded-lg transition duration-200 ${
                !isCurrentPoapCompleted || mintingLoading || alreadyMinted
                  ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                  : "bg-orange-600 text-white hover:bg-orange-700"
              }`}
            >
              {mintingLoading ? "Minting..." : alreadyMinted ? "Already Claimed" : isCurrentPoapCompleted ? "Claim NFT" : (<><MdLock className="mr-2" /> Minting Locked</>)}
            </button>

            {/* Simple tooltips (optional) */}
            {alreadyMinted && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 -translate-y-2 w-56 px-4 py-2 bg-gray-800 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 transition-opacity text-center">
                <span>You have already claimed this POAP.</span>
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full h-2 w-2 bg-gray-800 rotate-45" />
              </div>
            )}
          </div>
        </div>
      </Card>

      {showMintModal && (
        <MintConfirmationModal
          registeredEmail={userEmail}
          onConfirm={handleConfirmMint}
          onCancel={() => setShowMintModal(false)}
        />
      )}
    </div>
  );
};

export default Completed;
